import * as React from "react";
import { Box, Stack, Typography, Paper } from "@mui/material";
import RouterIcon from "@mui/icons-material/Router";
import CallIcon from "@mui/icons-material/Call";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExtensionIcon from "@mui/icons-material/Extension";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import ApiIcon from "@mui/icons-material/Api";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SupportAgent from "@mui/icons-material/SupportAgent";

function EmptyStateLayout({ icon, title, description, hint }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
            }}
        >
            <Stack spacing={2} alignItems="center" textAlign="center">
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 999,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "action.hover",
                    }}
                >
                    {icon}
                </Box>

                <Stack spacing={0.75}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 560 }}
                    >
                        {description}
                    </Typography>
                </Stack>

                {hint && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ maxWidth: 640 }}
                    >
                        {hint}
                    </Typography>
                )}
            </Stack>
        </Paper>
    );
}

/** Workers (machines that execute tasks) */
export function WorkersEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<RouterIcon sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="No workers connected yet"
            description="Workers are the machines that execute tasks for your agents."
            hint="Once a worker is connected, agents can run UI automation and local actions reliably."
        />
    );
}

/** Telephony */
export function TelephonyEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<CallIcon sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="Telephony is not configured"
            description="Connect a telephony provider to enable inbound/outbound calls and voice-enabled workflows for your agents."
            hint="After setup, agents can answer calls, verify information, and execute processes end-to-end."
        />
    );
}

/** API Keys */
export function ApiKeysEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<VpnKeyIcon sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="No API keys created"
            description="API keys let external systems securely integrate with Sophona widgets, services, and automation. Example: you can here add a key for OpenAi."
            hint="Use separate keys per environment (dev/staging/prod) for clean access control."
        />
    );
}

/** Processes (uploaded via Sophona Studio) */
export function ProcessesEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<AccountTreeIcon sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="No processes uploaded"
            description="Processes are built and packaged in Sophona Studio. After you upload a process, it will appear here automatically."
            hint="Tip: start with one small workflow, validate it in production, then expand."
        />
    );
}

/** Skills (uploaded via Sophona Studio) */
export function SkillsEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<ExtensionIcon sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="No skills uploaded"
            description="Skills are reusable building blocks created in Sophona Studio. Upload a skill package and it will show up here."
            hint="Skills can bundle UI steps, API calls, and integrations  then be reused across multiple agents."
        />
    );
}

export function JobsEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<WorkHistoryIcon sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="No jobs yet"
            description="This table shows jobs triggered manually or by the scheduler. Each job runs a selected process with specific inputs and produces an execution result."
            hint="Create a job by choosing a process and providing input parameters or let the scheduler trigger jobs automatically."
        />
    );
}

export function ApiEndpointsEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<ApiIcon sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="No API endpoints configured"
            description="API endpoints expose your processes and actions as callable routes. Use them to trigger executions from external systems or internal integrations."
            hint="Once created, endpoints can accept input payloads and return execution results making automation easy to integrate."
        />
    );
}

export function SchedulersEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<ScheduleIcon sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="No schedulers set up"
            description="Schedulers run jobs automatically on a defined schedule. They trigger selected processes with configured inputs."
            hint="Set up a scheduler to run recurring tasks (e.g. daily syncs, weekly reports, monitoring checks) without manual triggering."
        />
    );
}

export function AgentsEmptyStateInfo() {
    return (
        <EmptyStateLayout
            icon={<SupportAgent sx={{ fontSize: 34, color: "text.secondary" }} />}
            title="No agents created yet"
            description="Agents are your conversational personas equipped with execution layer, they can talk to users, follow instructions, and run skills and processes across your tools."
            hint="Create agents for different roles (e.g. support, operations, back office) and connect them to channels like chat or voice."
        />
    );
}